import { deviceAdminApi, graphicApi } from '@/api';
import { EditableCell, ModalConfirmationList } from '@/components/common';
import {
	buildActionColumn,
	buildColumn,
	ColumnsProps,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getSelectedRowKeysPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { getPermissionErrorMessage, SubPermissions, User } from '@/model/AccountModel';
import {
	BaseColumns,
	DefaultColumns,
	DefaultGridElement,
	PaginationSetting,
	ResponseObject,
	ResponseObjectEntity,
	ResponseStatusCode,
	SelectOptions,
} from '@/model/CommonModel';
import { DeviceObjectType, OptionsButtonBuilder, SelectedRowKeyPagination } from '@/model/DeviceAdminModel';
import { GraphicDirectoryInfo, GraphicType } from '@/model/GraphicModel';
import { Button, Col, Form, notification, Radio, RadioChangeEvent, Row, Spin, Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult, TableRowSelection } from 'antd/lib/table/interface';
import cx from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import styles from './graphicDirectoryListView.module.scss';

const user: User = getUser();
const mapsComponentPermission = User.getSubComponentPermission(user, SubPermissions.Maps_Maps_Use);
const graphicsComponentPermission = User.getSubComponentPermission(user, SubPermissions.Graphics_Graphics_Use);
const hasCorePermission = graphicsComponentPermission.allowed && user.isAdmin;

const scroll: ScrollType = { y: 378 };
const maxLength: number = 50;

type GraphicDirectoryInfoTable = GraphicDirectoryInfo & DefaultColumns & Partial<DefaultGridElement>;

type Props = {
	isModal: boolean;
	mapData: GraphicDirectoryInfo[];
	pagination: PaginationSetting;
	fetchAndSetMapDirectory: (graphicType: GraphicType, paginationSettings: PaginationSetting) => void;
	handleOnOpenMap: (data: Partial<GraphicDirectoryInfo>) => Promise<void>;
	handleOnOpenNewModal: () => void;
};

const createButtonOptions = (hasMoreThanOne: boolean, isItemOnSamePage: boolean): OptionsButtonBuilder<string> => {
	let opts = [];
	opts.push(
		{
			label: _('Publish'),
			disabled: true, // TODO: pending to define correct flow
			separator: false,
			value: 'publish',
			title: getPermissionErrorMessage(mapsComponentPermission.allowed, false, hasMoreThanOne),
			id: 'graphicMapDirectoryPublishOption',
		},
		{
			label: _('Rename'),
			disabled: hasMoreThanOne || !isItemOnSamePage,
			separator: false,
			value: 'rename',
			title: getPermissionErrorMessage(mapsComponentPermission.allowed, false, hasMoreThanOne),
			id: 'graphicMapDirectoryRenameOption',
		},
		{
			label: _('Delete'),
			disabled: false,
			separator: false,
			value: 'delete',
			title: getPermissionErrorMessage(mapsComponentPermission.allowed, false, hasMoreThanOne),
			id: 'graphicMapDirectoryDeleteOption',
		}
	);

	opts.flat();

	return {
		labelOrIcon: '...',
		options: opts,
	};
};

const GraphicDirectoryListView: FC<Props> = ({ mapData, fetchAndSetMapDirectory, pagination, isModal, handleOnOpenMap }) => {
	//#region State Management Variables
	const [mapDirectoryData, setMapDirectoryData] = useState<GraphicDirectoryInfoTable[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [fileTypeFilterMenu, setFileTypeFilterMenu] = useState<GraphicType>(GraphicType.Web);
	const [isItemOnSamePage, setIsItemOnSamePage] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedRowKeysPagination, setSelectedRowKeysPagination] = useState<SelectedRowKeyPagination[]>([]);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [editingKey, setEditingKey] = useState<React.Key>(undefined);

	const isSearchPerformed = useRef<boolean>(false);
	const [form] = Form.useForm();

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();
	//#endregion

	useEffect(() => {
		setMapDirectoryData(createTableData(mapData as GraphicDirectoryInfoTable[]));
	}, [mapData]);

	useEffect(() => {
		if (pagination.TotalItems === 0 && pagination.PageNumber > 1) {
			const { PageNumber: currentPage } = pagination;
			fetchAndSetMapDirectory(fileTypeFilterMenu, {
				...pagination,
				PageNumber: currentPage - 1,
			});
		}
	}, [pagination]);

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setIsItemOnSamePage(isSamePage);

		if (pageSize !== pagination.PageSize) {
			clearSelection();
		}
	};

	const disabled: boolean = editingKey !== undefined;
	const disabledActionButtons = disabled || selectedRowKeys.length === 0;
	const hasMoreThanOneKeySelected: boolean = selectedRowKeys.length > 1;
	const items = createButtonOptions(hasMoreThanOneKeySelected, isItemOnSamePage);
	const contextMenuOptions: SelectOptions<string>[] = items?.options ? [...items?.options] : [];
	const tablePagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		pagination.PageNumber,
		pagination.PageSize,
		pagination.TotalItems,
		undefined,
		selectedRowKeys
	);

	const columns: ColumnsProps<GraphicDirectoryInfoTable>[] = [
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			editable: true,
			sorter: !isEditMode,
			onCell: (record: GraphicDirectoryInfoTable) => ({
				record,
				dataIndex: 'Name',
				title: _('Name'),
				maxLength,
				options: fileTypeFilterMenu === GraphicType.Web ? contextMenuOptions : [],
				isTableInEditMode: isEditMode,
				onChangeSelection,
				onClickOption: handleActionScope,
				inputType: 'text',
				editing: isEditing(record),
			}),
		},
		{
			...buildColumn(_('Recent'), 'LastUpdated', '160px', 'start'),
			sorter: !isEditMode,
			onCell: (record: GraphicDirectoryInfoTable) => ({
				record,
				dataIndex: 'LastUpdated',
				title: _('Recent'),
				maxLength,
				options: fileTypeFilterMenu === GraphicType.Web ? contextMenuOptions : [],
				isTableInEditMode: isEditMode,
				onChangeSelection,
				onClickOption: handleActionScope,
			}),
		},
		{
			...buildColumn(_('Status'), 'Status', 'auto', 'start'),
			onCell: (record: GraphicDirectoryInfoTable) => ({
				record,
				dataIndex: 'Status',
				title: _('Status'),
				maxLength,
				options: fileTypeFilterMenu === GraphicType.Web ? contextMenuOptions : [],
				isTableInEditMode: isEditMode,
				onChangeSelection,
				onClickOption: handleActionScope,
			}),
			render: (text, record: GraphicDirectoryInfoTable) => _(`${GraphicType[record.Type]}`),
			sorter: !isEditMode,
		},
	];
	if (isEditMode) {
		columns.unshift(buildActionColumn(disabled));
	}

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys: React.Key[] = await getSelectedRowKeysForSelectAll();

		const cloneState: GraphicDirectoryInfoTable[] = mapDirectoryData.map(m => ({
			...m,
			checked: newSelectedRowKeys.findIndex(r => r === m.Id) !== -1,
		}));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			pagination.PageNumber,
			setIsItemOnSamePage
		);

		batch(() => {
			setSelectedRowKeysPagination(selectedKeysPagination);
			setSelectedRowKeys(newSelectedRowKeys);
			setIsTableLoading(false);
			setMapDirectoryData(createTableData(cloneState));
		});
	};

	const handleSelectInvert = () => {
		const dataKeys = mapDirectoryData.map<React.Key>(x => x.Id);
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);

		const cloneState: GraphicDirectoryInfoTable[] = mapDirectoryData.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.Id) !== -1,
		}));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			pagination.PageNumber,
			setIsItemOnSamePage
		);

		batch(() => {
			setMapDirectoryData(createTableData(cloneState));
			setSelectedRowKeysPagination(selectedKeysPagination);
			setSelectedRowKeys(newSelectedRowKeys);
		});
	};

	const handleOnChangeRowSelection = (selectedRowKeys: React.Key[]) => {
		const cloneState: GraphicDirectoryInfoTable[] = mapDirectoryData.map(x => ({
			...x,
			checked: selectedRowKeys.findIndex(r => r === x.Id) !== -1,
		}));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			selectedRowKeys,
			selectedRowKeysPagination,
			pagination.PageNumber,
			setIsItemOnSamePage
		);

		batch(() => {
			setMapDirectoryData(createTableData(cloneState));
			setSelectedRowKeysPagination(selectedKeysPagination);
			setSelectedRowKeys(selectedRowKeys);
		});
	};

	const rowSelection: TableRowSelection<GraphicDirectoryInfoTable> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `mapsTableCheckbox-${record.key}`,
			disabled,
			children: (
				<label htmlFor={`mapsTableCheckbox-${record.key}`} className={styles.srOnly}>
					{_('SelectMap')}
				</label>
			),
		}),
		selectedRowKeys,
		onChange: handleOnChangeRowSelection,
	};

	const createTableData = (data: GraphicDirectoryInfoTable[]): GraphicDirectoryInfoTable[] => {
		if (data?.length > 0) {
			return data.map(m => ({
				...m,
				Action: createActionElement(m),
				key: m.Id,
				LastUpdated: FormatDate(m.LastUpdated, false, null, true),
			}));
		}
		return [];
	};

	const handleOnSelectFilterMenu = (e: RadioChangeEvent) => {
		const filterValue: GraphicType = e.target.value;
		const newPagination: PaginationSetting = getDefaultPaginationSettings();
		batch(() => {
			setFileTypeFilterMenu(filterValue);
			setSelectedRowKeys([]);
			setSelectedRowKeysPagination([]);
			setIsEditMode(false);
			setEditingKey(undefined);
			form.resetFields();
			isSearchPerformed.current = false;
		});
		fetchAndSetMapDirectory(filterValue, newPagination);
	};

	const handleOnOpenMapDirectory = (mapData: GraphicDirectoryInfoTable): void => {
		setIsLoading(true);
		const dataToSend: GraphicDirectoryInfo = {
			CreatedByUserId: mapData.CreatedByUserId,
			Id: mapData.Id,
			LastUpdated: mapData.LastUpdated,
			LastUpdatedByUserId: mapData.LastUpdatedByUserId,
			Name: mapData.Name,
			Type: mapData.Type,
			Status: mapData.Status,
		};
		handleOnOpenMap(dataToSend).then(() => {
			setIsLoading(false);
		});
	};

	const getSelectedRowKeysForSelectAll = async (): Promise<React.Key[]> => {
		setIsTableLoading(true);
		const response: ResponseObjectEntity<number[]> = await graphicApi.getMapDirectoryByMapType(fileTypeFilterMenu);
		setIsTableLoading(false);
		if (handleResponse(response)) {
			return;
		}

		return getUniqueValuesArray(selectedRowKeys, response.Entity);
	};

	const clearSelection = () => {
		setSelectedRowKeys([]);
	};

	const createActionElement = (mapInfo: GraphicDirectoryInfoTable): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (mapInfo.editable) {
			const mapId = mapInfo.Id;
			content = (
				<>
					<Button id='renameSaveMapButton' key='save' type='primary' onClick={e => handleOnSaveEditRow(mapId)}>
						{_('Save')}
					</Button>
					<Button id='renameCancelMapButton' key='cancel' onClick={e => handleOnCancelEditRow(e, mapId)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}

		return <div className={styles.actions}>{content}</div>;
	};

	const getMapSelectedPage = async (mapId: number, paginationSettings: PaginationSetting) => {
		let newPage: number = paginationSettings.PageNumber;
		setIsTableLoading(true);
		const respNewPage = await graphicApi.getMapSelectedPage(mapId, fileTypeFilterMenu, paginationSettings);
		setIsTableLoading(false);

		if (!handleResponse(respNewPage)) {
			newPage = respNewPage.Entity;
		}

		const newPagination: PaginationSetting = {
			...paginationSettings,
			PageNumber: newPage,
		};

		batch(() => {
			setIsItemOnSamePage(true);
			setSelectedRowKeysPagination([{ Id: mapId, PaginationPage: newPage }]);
		});

		fetchAndSetMapDirectory(fileTypeFilterMenu, newPagination);
	};

	const handleOnSaveEditRow = async (mapId: number) => {
		const name = form.getFieldValue('Name');
		const map = mapDirectoryData.find(m => m.Id === mapId);
		if (map) {
			setIsTableLoading(true);
			let nameError;
			if (map.Type === GraphicType.VelocityCore) {
				nameError = await graphicApi.renameLegacyMap(mapId, name);
			} else {
				nameError = await deviceAdminApi.renameDevice(DeviceObjectType.Maps, name, mapId);
			}

			setIsTableLoading(false);
			if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
				form.setFields([
					{
						name: 'Name',
						errors: [nameError.ErrorMessage],
					},
				]);
				return;
			} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
				notification['error']({
					message: nameError.ResponseErrorDescription,
				});
				setEditingKey(undefined);
				setIsEditMode(false);
				fetchAndSetMapDirectory(fileTypeFilterMenu, pagination);
				return;
			}

			batch(() => {
				setEditingKey(undefined);
				setIsEditMode(false);
			});

			await getMapSelectedPage(map.Id, pagination);
		} else {
			batch(() => {
				setEditingKey(undefined);
				setIsEditMode(false);
			});
		}
	};

	const handleOnCancelEditRow = (e: React.MouseEvent<HTMLElement>, mapId: number) => {
		e.stopPropagation();
		const cloneState: GraphicDirectoryInfoTable[] = changeStateProps(mapId, { editable: false }, mapDirectoryData);
		setMapDirectoryData(cloneState);
		setIsEditMode(false);
		setEditingKey(undefined);
	};

	const handleOnChangeTable = (lPagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<GraphicDirectoryInfoTable>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination: lPagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: pagination,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			const newPagination: PaginationSetting = {
				...pagination,
				PageNumber: current,
				PageSize: pageSize,
				SortField: sortField.toString(),
				SortDirection: sortOrder,
			};
			fetchAndSetMapDirectory(fileTypeFilterMenu, newPagination);
		}
	};

	const onChangeSelection = (key: BaseColumns) => {
		if (fileTypeFilterMenu === GraphicType.Web) {
			const mapId = key.key;
			if (selectedRowKeys.findIndex(key => key === mapId) === -1) {
				const cloneState: GraphicDirectoryInfoTable[] = mapDirectoryData.map(x =>
					x.Id === mapId ? { ...x, checked: true } : { ...x, checked: false }
				);

				const selectedKeysPagination: SelectedRowKeyPagination = {
					Id: mapId,
					PaginationPage: pagination.PageNumber,
				};

				batch(() => {
					setMapDirectoryData(createTableData(cloneState));
					setIsItemOnSamePage(true);
					setSelectedRowKeysPagination([{ ...selectedKeysPagination }]);
					setSelectedRowKeys([mapId]);
				});
			}
		}
	};

	const getSelectedMap = async (mapId: number): Promise<GraphicDirectoryInfo> => {
		setIsTableLoading(true);
		const response: ResponseObjectEntity<GraphicDirectoryInfo> = await graphicApi.getMapBasicData(mapId, fileTypeFilterMenu);
		setIsTableLoading(false);

		if (handleResponse(response)) {
			return;
		}

		return response.Entity;
	};

	const onDeleteMapRequest = async (map: GraphicDirectoryInfoTable): Promise<void> => {
		setIsTableLoading(true);
		const response: ResponseObject = await graphicApi.deleteMap(map.Id, map.Type);
		setIsTableLoading(false);
		if (handleResponse(response)) {
			return;
		}
	};

	const handleDeleteOkConfirmation = async (selectedMaps: GraphicDirectoryInfoTable[]) => {
		for (const map of selectedMaps) {
			await onDeleteMapRequest(map);
		}
		setSelectedRowKeys([]);
		setSelectedRowKeysPagination([]);
		fetchAndSetMapDirectory(fileTypeFilterMenu, pagination);
	};

	const handleActionScope = async (key: string) => {
		if (key === 'delete') {
			const selectedMaps: GraphicDirectoryInfoTable[] = mapDirectoryData.filter(m => selectedRowKeys.includes(m.Id));
			ModalConfirmationList({
				data: selectedMaps,
				type: _('Maps'),
				onConfirm: () => handleDeleteOkConfirmation(selectedMaps),
			});
			return;
		}

		const selectedMapId = Number(selectedRowKeys?.[0]);
		const mapInfo: GraphicDirectoryInfo = await getSelectedMap(selectedMapId);

		switch (key) {
			case 'publish':
				// TODO: pending to define correct flow
				break;
			case 'rename':
				setIsEditMode(true);
				const findKey: GraphicDirectoryInfoTable = mapDirectoryData.find(x => x.key === mapInfo.Id);
				edit(findKey);
				const cloneState: GraphicDirectoryInfoTable[] = changeStateProps(mapInfo.Id, { editable: true }, mapDirectoryData);
				setMapDirectoryData(createTableData(cloneState));
				break;
		}
	};

	const changeStateProps = (
		mapId: number,
		mapInfo: Partial<GraphicDirectoryInfoTable>,
		mapDirectoryData: GraphicDirectoryInfoTable[]
	): GraphicDirectoryInfoTable[] => {
		const cloneState: GraphicDirectoryInfoTable[] = [...mapDirectoryData];
		const index = cloneState.findIndex(x => x.Id === mapId);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...mapInfo });
		}

		return cloneState;
	};

	const isEditing = (record: GraphicDirectoryInfoTable) => record.key === editingKey;

	const edit = (record: GraphicDirectoryInfoTable) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.key);
	};

	const canOpenMap = fileTypeFilterMenu === GraphicType.Web || (hasCorePermission && fileTypeFilterMenu === GraphicType.VelocityCore);

	return (
		<div className={cx(styles.modalContainer, { [styles.modalContainerModalMode]: isModal })}>
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
				<div className={styles.innerModalContainer}>
					<Row className={styles.tableAndFiltersContainer}>
						<Col flex='130px' className={styles.filterContainer}>
							<Radio.Group className={styles.filterMenu} value={fileTypeFilterMenu} onChange={handleOnSelectFilterMenu}>
								<Radio.Button value={GraphicType.Web}>{_('Web')}</Radio.Button>
								<Radio.Button
									value={GraphicType.VelocityCore}
									disabled={!hasCorePermission}
									title={getPermissionErrorMessage(hasCorePermission)}>
									{_('VelocityCore')}
								</Radio.Button>
							</Radio.Group>
						</Col>
						<Col flex='auto' className={styles.tableContainer}>
							<Form form={form} component={false}>
								<Table
									className={cx(styles.mapsTable, { [styles.tableModal]: isModal })}
									columns={columns}
									components={{
										body: {
											cell: EditableCell,
										},
									}}
									dataSource={createTableData(mapDirectoryData)}
									id='mapsTable'
									loading={isTableLoading}
									onChange={handleOnChangeTable}
									onRow={
										!isEditMode && canOpenMap
											? (record: GraphicDirectoryInfoTable) => ({
													onClick: () => {
														handleOnOpenMapDirectory(record);
													},
													title: getPermissionErrorMessage(canOpenMap),
											  })
											: null
									}
									pagination={tablePagination}
									rowSelection={rowSelection}
									scroll={scroll}
									size='small'
								/>
							</Form>
						</Col>
					</Row>
				</div>
			</Spin>
		</div>
	);
};

export { GraphicDirectoryListView };
